import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch a specific collection by pubkey and d-tag.
 */
export function useCollection(pubkey: string | undefined, dTag: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['collection', pubkey, dTag],
    queryFn: async ({ signal }) => {
      if (!pubkey || !dTag) return null;

      const events = await nostr.query(
        [{ kinds: [30005], authors: [pubkey], '#d': [dTag], limit: 1 }],
        { signal }
      );

      return events[0] ?? null;
    },
    enabled: !!pubkey && !!dTag,
    staleTime: 60000,
  });
}

/**
 * Hook to fetch all video events that are in a collection.
 */
export function useCollectionVideos(
  collection: NostrEvent | null | undefined,
  enabled: boolean = true
) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['collection-videos', collection?.id],
    queryFn: async ({ signal }) => {
      if (!collection) return [];

      // Extract video event IDs from the collection
      const videoIds = collection.tags
        .filter(([name]) => name === 'e')
        .map(([, eventId]) => eventId);

      if (videoIds.length === 0) return [];

      // Fetch the actual video events
      const events = await nostr.query(
        [{ kinds: [21], ids: videoIds }],
        { signal }
      );

      // Sort by the order they appear in the collection
      const orderedEvents = videoIds
        .map(id => events.find(e => e.id === id))
        .filter((e): e is NostrEvent => !!e);

      return orderedEvents;
    },
    enabled: enabled && !!collection,
    staleTime: 60000,
  });
}